import classNames from 'classnames';
import {
  type ChangeEvent,
  cloneElement,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { isKeyboardFocus } from '../../utils/is-keyboard-focus';
import styles from './Checkbox.module.css';
import { toggleGroupValue } from './CheckboxGroup';
import { useCheckboxGroupContext } from './CheckboxGroupContext';
import { CheckboxIndicator } from './CheckboxIndicator';

export interface CheckboxProps
  extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  /**
   * Opt-in controlled mode. With a handler the input toggles itself and
   * reports each change here with the native event (`event.target.checked`),
   * while the parent owns `checked`. Without one the input stays `readOnly`
   * and the surrounding row drives it, as the selection tree does.
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Inside a `CheckboxGroup`, the entry this box stands for: it is checked
   * when the group's value includes it, and toggling adds or removes it
   * there. Outside a group the value only lands on the input.
   */
  value?: string;
  /**
   * Optional glyph rendered inside the box when checked/indeterminate, in place
   * of the indicator's default check/minus. Cloned with `isChecked` /
   * `isIndeterminate` so it can mirror the control's state — mirrors the old
   * Chakra `icon` prop contract.
   */
  icon?: ReactElement<{ isChecked?: boolean; isIndeterminate?: boolean }>;
  /** See `CheckboxIndicator` — for rows that remount on every selection change. */
  animateCheck?: boolean;
  /** Label font size from the type scale (`sm` 14px, `md` 16px). Without it
   *  the label takes the surrounding font size. */
  labelSize?: 'sm' | 'md';
  children?: ReactNode;
}

/**
 * Chakra-free checkbox: a `<label>` wrapping a visually-hidden native input
 * plus the presentational `CheckboxIndicator`. The native input carries the
 * checked/disabled state (and `indeterminate`, set via ref since it is a DOM
 * property, not an attribute). Extra props (`data-testid`, `className`, …)
 * land on the label root — matching where Chakra forwarded them — so specs
 * that click must target the input (`getByRole('checkbox')`), not the testid.
 * `aria-label` is the exception: it names the input, for a box with no
 * visible label.
 *
 * Two modes. Row-driven (no `onChange`, no group): the input is `readOnly`
 * and the surrounding row toggles the state. Controlled (`onChange`, or a
 * `value` inside a `CheckboxGroup`): the input is live, one click or Space
 * fires one change, and the indicator shows hover and focus.
 */
export const Checkbox = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  value,
  icon,
  animateCheck,
  labelSize,
  children,
  className,
  onMouseEnter,
  onMouseLeave,
  'aria-label': ariaLabel,
  ...rest
}: CheckboxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const group = useCheckboxGroupContext();
  const inGroup = group !== null && value !== undefined;
  // Row-driven consumers pass neither, so they keep the read-only render.
  const isInteractive = inGroup || onChange !== undefined;
  const isChecked = inGroup ? group.value.includes(value) : checked;
  // A disabled group greys out every box inside it, with or without a `value`.
  const isDisabled = disabled || (group?.disabled ?? false);
  const isIndeterminate = indeterminate && !isChecked;

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // A focused input that becomes disabled (or read-only) loses focus without a
  // blur event, so the ring would stay; clear it in the same render.
  if (isFocused && (isDisabled || !isInteractive)) {
    setIsFocused(false);
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // A click clears the input's `indeterminate` natively; put the prop's value
    // back, since the effect above only runs when that value changes.
    event.target.indeterminate = isIndeterminate;
    if (inGroup) {
      group.onChange(
        toggleGroupValue(group.value, value, event.target.checked)
      );
    }
    onChange?.(event);
  };

  return (
    <label
      className={classNames(styles.root, className)}
      data-checkbox-root=""
      data-disabled={isDisabled ? '' : undefined}
      onMouseEnter={
        isInteractive
          ? (event) => {
              setIsHovered(true);
              onMouseEnter?.(event);
            }
          : onMouseEnter
      }
      onMouseLeave={
        isInteractive
          ? (event) => {
              setIsHovered(false);
              onMouseLeave?.(event);
            }
          : onMouseLeave
      }
      {...rest}
    >
      <input
        ref={inputRef}
        type="checkbox"
        className={styles.input}
        aria-label={ariaLabel}
        checked={isChecked}
        disabled={isDisabled}
        value={value}
        readOnly={!isInteractive}
        onChange={isInteractive ? handleChange : undefined}
        onFocus={
          isInteractive
            ? (event) => setIsFocused(isKeyboardFocus(event.target))
            : undefined
        }
        // A key press after a mouse click switches the browser to keyboard
        // focus (`:focus-visible` turns on at keydown), so re-read it here.
        onKeyDown={
          isInteractive
            ? (event) => setIsFocused(isKeyboardFocus(event.target))
            : undefined
        }
        onBlur={isInteractive ? () => setIsFocused(false) : undefined}
      />
      <CheckboxIndicator
        checked={isChecked}
        indeterminate={indeterminate}
        disabled={isDisabled}
        isHovered={isHovered}
        isFocused={isFocused}
        animateCheck={animateCheck}
      >
        {icon && isValidElement(icon)
          ? cloneElement(icon, {
              isChecked,
              isIndeterminate,
            })
          : undefined}
      </CheckboxIndicator>
      {children != null && (
        <span
          className={classNames(styles.label, labelSize && styles[labelSize])}
        >
          {children}
        </span>
      )}
    </label>
  );
};
